import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus, X, Save, HelpCircle } from 'lucide-react';
import axios from 'axios';

const AdminTooltips = () => {
  const [tooltips, setTooltips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTooltip, setEditingTooltip] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    title: '',
    help_text: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchTooltips();
  }, []);

  const fetchTooltips = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/tooltips`);
      setTooltips(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des tooltips:', err);
      setError('Impossible de charger les tooltips');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTooltip(null);
    setFormData({ key: '', title: '', help_text: '' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (tooltip) => {
    setEditingTooltip(tooltip);
    setFormData({
      key: tooltip.key,
      title: tooltip.title,
      help_text: tooltip.help_text
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTooltip(null);
    setFormData({ key: '', title: '', help_text: '' });
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      if (editingTooltip) {
        // Mise à jour
        await axios.put(`${backendUrl}/api/tooltips/${editingTooltip.id}`, {
          title: formData.title,
          help_text: formData.help_text
        });
        setSuccessMessage('Tooltip mis à jour avec succès');
      } else {
        // Création
        await axios.post(`${backendUrl}/api/tooltips`, formData);
        setSuccessMessage('Tooltip créé avec succès');
      }
      
      fetchTooltips();
      closeModal();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleDelete = async (tooltipId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tooltip ?')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/tooltips/${tooltipId}`);
      setSuccessMessage('Tooltip supprimé avec succès');
      fetchTooltips();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le tooltip');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des bulles d'aide</h1>
            <p className="text-gray-600 mt-2">Gérez les textes d'aide affichés sur le site</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-celadon text-white px-6 py-3 rounded-lg hover:bg-celadon-light transition-colors"
          >
            <Plus size={20} />
            Nouveau tooltip
          </button>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg">
            {successMessage}
          </div>
        )}

        {error && !showModal && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement...</p>
            </div>
          ) : tooltips.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              Aucun tooltip pour le moment
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Texte d'aide
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tooltips.map((tooltip) => (
                  <tr key={tooltip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm text-gray-900 bg-gray-100 px-2 py-1 rounded">
                        {tooltip.key}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <HelpCircle size={16} className="text-celadon" />
                        {tooltip.title}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-md truncate">
                        {tooltip.help_text}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(tooltip)}
                        className="text-celadon hover:text-celadon-light mr-4"
                        title="Éditer"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(tooltip.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingTooltip ? 'Éditer le tooltip' : 'Nouveau tooltip'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Clé (non éditable si modification) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clé unique *
                    </label>
                    <input
                      type="text"
                      name="key"
                      value={formData.key}
                      onChange={handleInputChange}
                      disabled={!!editingTooltip}
                      placeholder="ex: dimensions-sur-mesure"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      La clé ne peut pas être modifiée après la création
                    </p>
                  </div>

                  {/* Titre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="ex: Dimensions sur mesure"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
                      required
                    />
                  </div>

                  {/* Texte d'aide */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Texte d'aide *
                    </label>
                    <textarea
                      name="help_text"
                      value={formData.help_text}
                      onChange={handleInputChange}
                      placeholder="Entrez le texte d'aide qui sera affiché..."
                      rows="6"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon resize-none"
                      required
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-celadon text-white py-3 px-6 rounded-lg hover:bg-celadon-light transition-colors flex items-center justify-center gap-2"
                  >
                    <Save size={20} />
                    {editingTooltip ? 'Enregistrer' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTooltips;
